// The committed-budget tier controls, on the System Admin page.
//
// One row per registered tier (today: the human-handoff / Calendly tier): what
// it gates, where it sits, and a number field to move it. The write is TWO
// steps (type, then a pending bar that names the exact change, asks for a
// reason and applies) because this control decides which clients get handed
// to a human desk and offered the Calendly scheduler - a field that commits on
// blur would let a slipped keystroke hand off every project on the platform.
// Same tone as the Red Button and the A/B dials beside it: consequential
// controls state their effect before they have one.
//
// Read-only System Admins see the card with the field disabled - the tier's
// position is the read half of the area, moving it is the full half - matching
// PlatformControl's canAct/accessKnown contract on the same page.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Input } from "@recruiter/shared";
import type { AdminBudgetThreshold } from "@recruiter/shared";

import { setBudgetThreshold } from "../lib/api";
import { adminBudgetThresholdsQuery, adminKeys } from "../lib/queries";
import { AreaBoundary } from "./area-gate";
import { LoadingState, errorMessage } from "./states";
import "./budget-thresholds.css";

type Props = {
  /** May this operator actually move a tier? `system_admin` at `full`. */
  canAct: boolean;
  /** Has the grant query answered yet? See PlatformControl for why the
   *  read-only sentence must wait for this while the controls need not. */
  accessKnown: boolean;
};

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** Whole non-negative dollars, or null for anything the field can hold that
 *  the server would refuse. Exported for the unit test. */
export function parseThresholdInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d{1,8}$/.test(trimmed)) return null;
  return Number(trimmed);
}

export function BudgetThresholds({ canAct, accessKnown }: Props) {
  const thresholds = useQuery(adminBudgetThresholdsQuery());

  return (
    <section className="ad-section">
      <h2 className="ad-section__title">Budget thresholds</h2>
      <p className="bt-thresholds__sub">
        The committed project budgets that change how Mira treats a client. Each is whole US dollars
        against the client&apos;s whole commitment: a fixed price, a stated total, or a rate
        multiplied out; an open-ended retainer is judged on one month. Changes reach every server
        within about fifteen seconds and are kept as history.
        {accessKnown && !canAct ? " Your access is read only, so the fields are disabled." : null}
      </p>
      {thresholds.isPending ? <LoadingState label="Reading the thresholds…" /> : null}
      {thresholds.isError ? (
        <AreaBoundary
          area="system_admin"
          error={thresholds.error}
          onRetry={() => void thresholds.refetch()}
        />
      ) : null}
      {thresholds.data ? (
        <div className="bt-thresholds__list">
          {thresholds.data.thresholds.map((threshold) => (
            <ThresholdRow key={threshold.key} threshold={threshold} canAct={canAct} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ThresholdRow({ threshold, canAct }: { threshold: AdminBudgetThreshold; canAct: boolean }) {
  const queryClient = useQueryClient();
  // null = the field shows what the server says; a string = an unapplied edit.
  const [draft, setDraft] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const shown = draft ?? String(threshold.usd);
  const parsed = draft === null ? threshold.usd : parseThresholdInput(draft);
  const dirty = draft !== null && parsed !== threshold.usd;
  const invalid = draft !== null && parsed === null;
  const canApply = dirty && parsed !== null && reason.trim().length >= 3;

  const apply = useMutation({
    mutationFn: () => {
      if (parsed === null) throw new Error("Enter a whole number of dollars.");
      return setBudgetThreshold(threshold.key, parsed, reason.trim());
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(adminKeys.budgetThresholds(), fresh);
      setDraft(null);
      setReason("");
    },
  });

  const discard = () => {
    setDraft(null);
    setReason("");
    apply.reset();
  };

  return (
    <div className="bt-thresholds__row">
      <div className="bt-thresholds__meta">
        <div className="bt-thresholds__name">
          <span className="bt-thresholds__label">{threshold.label}</span>
          <Badge tone={threshold.usd > 0 ? "success" : "neutral"}>
            {threshold.usd > 0 ? `${usd.format(threshold.usd)} and up` : "off (0)"}
          </Badge>
          {threshold.usd !== threshold.defaultUsd ? (
            <Badge tone="warning">default {usd.format(threshold.defaultUsd)}</Badge>
          ) : null}
        </div>
        <p className="bt-thresholds__blurb">{threshold.blurb}</p>
        <p className="bt-thresholds__audit">
          {threshold.setBy && threshold.setAt
            ? `Last moved by ${threshold.setBy} on ${new Date(threshold.setAt).toLocaleString()}${
                threshold.reason ? ` — "${threshold.reason}"` : ""
              }.`
            : "Never moved; sitting at the configured default."}
        </p>
      </div>

      <div className="bt-thresholds__control">
        <span className="bt-thresholds__currency" aria-hidden="true">
          $
        </span>
        <Input
          className="bt-thresholds__field"
          inputMode="numeric"
          value={shown}
          disabled={!canAct || apply.isPending}
          aria-label={`${threshold.label}, minimum committed budget in US dollars`}
          aria-invalid={invalid || undefined}
          onChange={(event) => setDraft(event.target.value)}
        />
      </div>

      {dirty || invalid ? (
        <div className="bt-thresholds__pending" role="status">
          <span className="bt-thresholds__pending-text">
            {invalid
              ? "Enter a whole number of dollars (0 turns the handoff off)."
              : `Move ${threshold.label} from ${usd.format(threshold.usd)} to ${usd.format(parsed ?? 0)}?`}
          </span>
          {!invalid ? (
            <Input
              className="bt-thresholds__reason"
              value={reason}
              placeholder="Why? (kept with the change)"
              disabled={apply.isPending}
              aria-label="Reason for the change"
              onChange={(event) => setReason(event.target.value)}
            />
          ) : null}
          <Button
            size="sm"
            tone="accent"
            disabled={!canApply || apply.isPending}
            onClick={() => apply.mutate()}
          >
            {apply.isPending ? "Applying…" : `Apply ${usd.format(parsed ?? 0)}`}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            disabled={apply.isPending}
            onClick={discard}
          >
            Discard
          </Button>
        </div>
      ) : null}
      {apply.isError ? <p className="bt-thresholds__error">{errorMessage(apply.error)}</p> : null}
    </div>
  );
}
